import React, { useState, useEffect } from 'react';
import './TeamCreationPopup.css';
import { authService } from '../services/auth';
import config from '../config';

const TeamCreationPopup = ({ userEmail, onClose, onSuccess }) => {
  const [companyName, setCompanyName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [inviteEmails, setInviteEmails] = useState([]);  // Changed to array for chip UI
  const [currentEmailInput, setCurrentEmailInput] = useState('');  // For current email being typed
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Pre-fill company info on mount
  useEffect(() => {
    const prefillCompanyInfo = async () => {
      if (!userEmail) return;
      
      try {
        const response = await fetch(`${config.apiUrl}/api/team/check-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          },
          body: JSON.stringify({ email: userEmail })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.is_work_email) {
            setCompanyName(data.company_name || '');
            setCompanyWebsite(data.company_website || '');
          }
        }
      } catch (err) {
        console.error('Error fetching company info:', err);
      }
    };
    
    prefillCompanyInfo();
  }, [userEmail]);

  const handleCreateTeamAndInvite = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Step 1: Create team
      const createResponse = await fetch(`${config.apiUrl}/api/team/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          company_name: companyName,
          company_website: companyWebsite
        })
      });
      
      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.error || 'Failed to create team');
      }
      
      await createResponse.json();
      
      // Step 2: Send invites if there are emails
      if (inviteEmails.length > 0) {
        const inviteResponse = await fetch(`${config.apiUrl}/api/team/invite`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          },
          body: JSON.stringify({
            emails: inviteEmails
          })
        });
        
        if (!inviteResponse.ok) {
          const errorData = await inviteResponse.json();
          throw new Error(errorData.error || 'Failed to send invitations');
        }
        
        const inviteData = await inviteResponse.json();
        console.log('Invitations sent successfully:', inviteData);
        
        // Set loading to false first to allow success message to render
        setIsLoading(false);
        
        // Then set success message
        setSuccessMessage(`Team created! Invited ${inviteData.invited.length} team member(s).`);
      } else {
        setIsLoading(false);
        setSuccessMessage(`Team created for ${companyName}!`);
      }
      
      // Don't call onSuccess immediately - wait for success message to show
      setTimeout(() => {
        // Update parent component with new company info
        onSuccess({
          company_name: companyName,
          company_website: companyWebsite
        });
      }, 1000);  // Wait 1 second before updating parent
      
      // Close popup after showing success message
      setTimeout(() => {
        onClose();
      }, 4000);  // Show success for 4 seconds total
      
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  // Handle adding email when typing
  const handleEmailInputKeyDown = (e) => {
    const email = currentEmailInput.trim();
    
    if ((e.key === 'Enter' || e.key === ',' || e.key === ' ' || e.key === 'Tab') && email) {
      e.preventDefault();
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(email)) {
        // Use functional update to ensure we have the latest state
        setInviteEmails(prevEmails => {
          if (!prevEmails.includes(email)) {
            setError(null);
            return [...prevEmails, email];
          } else {
            setError('Email already added');
            return prevEmails;
          }
        });
        setCurrentEmailInput('');
      } else {
        setError('Please enter a valid email address');
      }
    }
  };
  
  // Handle pasting multiple emails
  const handleEmailPaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const emails = pastedText.split(/[,\n\s]+/).filter(email => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email.trim());
    });
    
    if (emails.length > 0) {
      // Use functional update to ensure we have the latest state
      setInviteEmails(prevEmails => {
        const newEmails = emails.filter(email => !prevEmails.includes(email));
        return newEmails.length > 0 ? [...prevEmails, ...newEmails] : prevEmails;
      });
      setCurrentEmailInput('');
    }
  };
  
  // Remove email chip
  const removeEmail = (emailToRemove) => {
    setInviteEmails(prevEmails => prevEmails.filter(email => email !== emailToRemove));
  };
  

  return (
    <div className="team-popup-overlay">
      <div className="team-popup">
        {/* Success Overlay - appears on top when successMessage is set */}
        {successMessage && (
          <div className="success-overlay">
            <div className="success-content">
              <div className="success-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="22" stroke="#10B981" strokeWidth="3"/>
                  <path d="M15 24L21 30L33 18" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>{successMessage}</h3>
              <p className="success-subtext">Redirecting to dashboard...</p>
            </div>
          </div>
        )}
        
        {/* Removed X button - users must use Skip or Continue */}
        
        <h2>Set Up Your Team</h2>
        <p className="team-popup-subtitle">
          You&apos;re using a work email. Create your team workspace and invite colleagues to collaborate.
        </p>
        
        <div className="team-popup-form">
          <div className="form-group">
            <label htmlFor="company-name">Company Name</label>
            <input
              id="company-name"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Enter your company name"
              disabled={isLoading}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="company-website">Company Website</label>
            <input
              id="company-website"
              type="url"
              value={companyWebsite}
              onChange={(e) => setCompanyWebsite(e.target.value)}
              placeholder="https://example.com"
              disabled={isLoading}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="invite-emails">Invite Team Members</label>
            <div 
              className="email-chip-container"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                // Focus the input when clicking the container
                if (e.target === e.currentTarget || e.target.classList.contains('email-chip-container')) {
                  document.getElementById('invite-emails').focus();
                }
              }}
              onKeyDown={(e) => {
                // Allow keyboard activation with Enter or Space
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  document.getElementById('invite-emails').focus();
                }
              }}
            >
              {inviteEmails.map((email, index) => (
                <div key={index} className="email-chip">
                  <span>{email}</span>
                  <button 
                    className="email-chip-remove" 
                    onClick={() => removeEmail(email)}
                    disabled={isLoading}
                    type="button"
                  >
                    ×
                  </button>
                </div>
              ))}
              <input
                id="invite-emails"
                type="text"
                value={currentEmailInput}
                onChange={(e) => setCurrentEmailInput(e.target.value)}
                onKeyDown={handleEmailInputKeyDown}
                onPaste={handleEmailPaste}
                placeholder={inviteEmails.length === 0 ? "Enter email addresses..." : "Add another email..."}
                disabled={isLoading}
                className="email-chip-input"
                autoComplete="off"
              />
            </div>
            <small className="form-hint">
              Press Enter, Space, or Comma after each email. You can paste multiple emails at once.
            </small>
          </div>
          
          {error && <div className="error-message">{error}</div>}
          {/* TODO: use or automatically clear this every now and then if the user is a company that need to setup a team localStorage.removeItem('team_creation_dismissed') */}
          <div className="popup-actions">
            <button 
              className="btn-secondary" 
              onClick={onClose}
              disabled={isLoading}
            >
              Skip for Now
            </button>
            <button 
              className="btn-primary" 
              onClick={handleCreateTeamAndInvite}
              disabled={isLoading || !companyName || !companyWebsite}
            >
              {isLoading ? 'Creating Team...' : 'Create Team & Send Invites'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamCreationPopup;
